import React from 'react';
import {Node, Edge, useNodes, useEdges, useReactFlow} from 'reactflow';

import {LayoutSettings} from '../layout/calculateDimensionsForLayout';
import layoutFlowchart, {FlowchartData} from '../layout/layoutFlowchart';
import BaseControlButton from './BaseControlButton';
import {ZOOM_ANIMATION_DURATION} from './HorizontalControls';

interface ControlledAutoLayoutButtonProps<N extends Node, E extends Edge> {
  flowchartData: FlowchartData<N, E>;
  customLayoutSettings?: Partial<LayoutSettings<N, E>>;
  setNodes: (nodes: Array<N>) => void;
  setEdges: (edges: Array<E>) => void;
}

export function ControlledAutoLayoutButton<N extends Node, E extends Edge>(
  props: ControlledAutoLayoutButtonProps<N, E>
): JSX.Element {
  const {flowchartData, customLayoutSettings} = props;
  const {fitView} = useReactFlow();

  return (
    <BaseControlButton
      icon="magic-wand"
      tooltip="Auto layout"
      onClick={() => {
        const {nodes, edges} = layoutFlowchart(flowchartData, customLayoutSettings);
        props.setNodes(nodes);
        props.setEdges(edges);
        // Defer the fitView to the next tick to ensure that the nodes have been rendered and measured.
        setTimeout(() => {
          fitView({duration: ZOOM_ANIMATION_DURATION});
        });
      }}
    />
  );
}

export function UncontrolledAutoLayoutButton<N extends Node, E extends Edge>(props: {
  customLayoutSettings?: Partial<LayoutSettings<N, E>>;
}): JSX.Element {
  const nodes = useNodes();
  const edges = useEdges();
  const {setNodes, setEdges} = useReactFlow();

  return (
    <ControlledAutoLayoutButton
      flowchartData={{nodes, edges}}
      customLayoutSettings={props.customLayoutSettings}
      setNodes={setNodes}
      setEdges={setEdges}
    />
  );
}
